import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Save, Copy, Check, Plus, X } from "lucide-react";
import AppShell, { getActiveSchemeId } from "@/components/AppShell";
import { api, apiError } from "@/lib/api";
import ConfirmationGate, { useConfirmationGate } from "@/components/ConfirmationGate";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export default function SchemeSettings() {
  const navigate = useNavigate();
  const [scheme, setScheme] = useState(null);
  const [lots, setLots] = useState([]);
  const [members, setMembers] = useState([]);
  const [error, setError] = useState(null);

  // Patch scheme state
  const [editName, setEditName] = useState("");
  const [editAddr, setEditAddr] = useState("");
  const [editPlan, setEditPlan] = useState("");
  const [savingScheme, setSavingScheme] = useState(false);

  // Invite state
  const [invEmail, setInvEmail] = useState("");
  const [invRole, setInvRole] = useState("committee");
  const [invLotIds, setInvLotIds] = useState([]);
  const [creatingInvite, setCreatingInvite] = useState(false);
  const [lastInvite, setLastInvite] = useState(null);
  const [copied, setCopied] = useState(false);

  // Sprint 2H Phase B — MEMBER_INVITE confirmation gate. On confirm, the
  // backend response carries {membership_id, invite_token, invite_url, ...};
  // ConfirmationGate now propagates the response through onConfirmed so we can
  // render the copy-link UI without a second round-trip.
  const inviteGate = useConfirmationGate({
    onConfirmed: (data) => {
      if (!data) return;
      const fullUrl = `${window.location.origin}/invite/${data.invite_token}`;
      setLastInvite({ ...data, full_url: fullUrl });
      setInvEmail("");
      setInvLotIds([]);
      load();
      // UX polish sprint — user reported the invite flow "didn't work" because
      // no toast fires on the post-gate confirm path. Adding an explicit
      // success toast + honest indicator that email delivery is currently mocked.
      toast.success("Invite created — copy the link below to share manually", { duration: 6000 });
    },
  });

  const load = useCallback(async () => {
    const id = getActiveSchemeId();
    if (!id) {
      navigate("/onboarding");
      return;
    }
    try {
      const [s, l, m] = await Promise.all([
        api.get(`/schemes/${id}`),
        api.get(`/schemes/${id}/lots`),
        api.get(`/schemes/${id}/members`),
      ]);
      setScheme(s.data);
      setEditName(s.data.name);
      setEditAddr(s.data.address);
      setEditPlan(s.data.plan_number);
      setLots(l.data);
      setMembers(m.data);
      if (s.data.my_role !== "chair") {
        navigate("/dashboard");
      }
    } catch (e) {
      if (e?.response?.status === 401) navigate("/login");
      else if (e?.response?.status === 403) navigate("/dashboard");
      else setError(apiError(e));
    }
  }, [navigate]);

  useEffect(() => {
    load();
  }, [load]);

  const saveScheme = async (e) => {
    e.preventDefault();
    setSavingScheme(true);
    try {
      const { data } = await api.patch(`/schemes/${scheme.scheme_id}`, {
        name: editName,
        address: editAddr,
        plan_number: editPlan,
      });
      setScheme(data);
      toast.success("Scheme updated");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setSavingScheme(false);
    }
  };

  const saveLot = async (lot) => {
    try {
      const { data } = await api.patch(
        `/schemes/${scheme.scheme_id}/lots/${lot.lot_id}`,
        {
          unit_entitlement: Number(lot.lot_entitlement ?? lot.unit_entitlement),
          lot_entitlement: Number(lot.lot_entitlement ?? lot.unit_entitlement),
          interest_entitlement: Number(lot.interest_entitlement ?? 1),
          address_unit: lot.address_unit || null,
          notes: lot.notes || null,
        }
      );
      setLots((prev) =>
        prev.map((l) => (l.lot_id === lot.lot_id ? data : l))
      );
      toast.success(`Lot ${lot.lot_number} saved`);
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const toggleLotForInvite = (lotId) => {
    setInvLotIds((prev) =>
      prev.includes(lotId) ? prev.filter((x) => x !== lotId) : [...prev, lotId]
    );
  };

  const createInvite = async (e) => {
    e.preventDefault();
    setCreatingInvite(true);
    try {
      // Sprint 2H Phase B — MEMBER_INVITE gate. On first click the backend
      // returns 400 confirmation_required; `fire()` opens ConfirmationGate and
      // returns null. On confirm the invite is created and `onConfirmed(data)`
      // receives the invite response so we can render the copy-link UI.
      const data = await inviteGate.fire(
        "post",
        `/schemes/${scheme.scheme_id}/invites`,
        {
          email: invEmail,
          role: invRole,
          lot_ids: invRole === "owner" ? invLotIds : null,
        },
        { __scheme_id: scheme.scheme_id },
      );
      if (data) {
        // Pre-attached confirmation happy path (rare from stock UI).
        const fullUrl = `${window.location.origin}/invite/${data.invite_token}`;
        setLastInvite({ ...data, full_url: fullUrl });
        setInvEmail("");
        setInvLotIds([]);
        toast.success("Invite created");
        load();
      }
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setCreatingInvite(false);
    }
  };

  const removeMember = async (m) => {
    if (!window.confirm(`Remove ${m.email} from this scheme?`)) return;
    try {
      await api.delete(`/schemes/${scheme.scheme_id}/members/${m.membership_id}`);
      toast.success("Member removed");
      load();
    } catch (err) {
      toast.error(apiError(err));
    }
  };

  const copyInvite = async () => {
    if (!lastInvite?.full_url) return;
    await navigator.clipboard.writeText(lastInvite.full_url);
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  if (error)
    return (
      <AppShell>
        <div className="max-w-6xl mx-auto px-6 py-10">
          <div className="rounded-lg p-4 text-sm" style={{ background: "var(--alert-light)", color: "var(--alert)" }}>
            {error}
          </div>
        </div>
      </AppShell>
    );
  if (!scheme) return <AppShell><div className="min-h-[40vh]" /></AppShell>;

  return (
    <AppShell>
      <div className="max-w-6xl mx-auto px-5 lg:px-8 py-8 lg:py-10 space-y-6" data-testid="scheme-settings-page">
        <div>
          <h1 className="text-2xl font-bold" style={{ color: "var(--navy)" }}>Scheme settings</h1>
          <p className="text-sm" style={{ color: "var(--slate)" }}>
            Chair-only — update details, manage lots, invite members.
          </p>
        </div>

        {/* ---- Scheme details ---- */}
        <Card style={{ borderColor: "var(--border)" }}>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Details</CardTitle>
            <CardDescription className="text-xs">State and lot count are fixed after creation.</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={saveScheme} className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Field id="dname" label="Scheme name">
                <Input id="dname" value={editName} onChange={(e) => setEditName(e.target.value)} required data-testid="settings-name-input" />
              </Field>
              <Field id="dplan" label="Plan number">
                <Input id="dplan" value={editPlan} onChange={(e) => setEditPlan(e.target.value)} required data-testid="settings-plan-input" />
              </Field>
              <Field id="daddr" label="Address" className="md:col-span-2">
                <Input id="daddr" value={editAddr} onChange={(e) => setEditAddr(e.target.value)} required data-testid="settings-address-input" />
              </Field>
              <div className="md:col-span-2 flex justify-end">
                <Button type="submit" disabled={savingScheme} style={{ background: "var(--blue)", color: "white" }} className="h-10 font-semibold" data-testid="settings-save-btn">
                  <Save size={14} className="mr-1.5" />
                  {savingScheme ? "Saving…" : "Save details"}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>

        {/* ---- Lots ---- */}
        <Card style={{ borderColor: "var(--border)" }}>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Lots</CardTitle>
            <CardDescription className="text-xs">{lots.length} lots · edit lot entitlement, interest entitlement, unit number, notes.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            {(() => {
              const totalLot = lots.reduce((s, l) => s + Number(l.lot_entitlement ?? l.unit_entitlement ?? 0), 0);
              const totalInt = lots.reduce((s, l) => s + Number(l.interest_entitlement ?? 0), 0);
              const looksOff = (n) => n !== 100 && n !== 1000;
              return (
                <div className="flex items-center gap-4 text-xs font-mono pb-2 border-b mb-2" style={{ borderColor: "var(--border)" }} data-testid="lots-totals">
                  <span data-testid="lots-total-entitlement">Total lot entitlement: <strong style={{ color: looksOff(totalLot) ? "var(--warning)" : "var(--success)" }}>{totalLot}</strong></span>
                  <span data-testid="lots-total-interest">Total interest entitlement: <strong style={{ color: looksOff(totalInt) ? "var(--warning)" : "var(--success)" }}>{totalInt}</strong></span>
                  {(looksOff(totalLot) || looksOff(totalInt)) && (
                    <span className="text-[10px] italic" style={{ color: "var(--warning)" }} data-testid="lots-total-warning">(typical totals are 100 or 1000 — verify if intentional)</span>
                  )}
                </div>
              );
            })()}
            {lots.map((lot, idx) => (
              <LotRow
                key={lot.lot_id}
                lot={lot}
                onChange={(patch) =>
                  setLots((prev) => prev.map((l, i) => (i === idx ? { ...l, ...patch } : l)))
                }
                onSave={() => saveLot(lots[idx])}
              />
            ))}
          </CardContent>
        </Card>

        {/* ---- Invite ---- */}
        <Card style={{ borderColor: "var(--border)" }}>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Invite members</CardTitle>
            <CardDescription className="text-xs text-slate-600">
              Send someone an invite to this scheme. They&apos;ll accept it via the shared link.
            </CardDescription>
          </CardHeader>
          <CardContent>
            {/* UX polish sprint — honest indicator that email delivery is currently mocked. */}
            <div
              className="mb-4 rounded-md border p-3 flex items-start gap-2.5 text-xs"
              style={{ background: "#fef3c7", borderColor: "#fbbf24", color: "#78350f" }}
              data-testid="invite-email-mocked-banner"
              role="status"
            >
              <span className="font-semibold shrink-0">Heads up:</span>
              <span>
                Email delivery is <strong>currently disabled</strong> in this environment (Resend not configured).
                Create the invite below and share the generated link with the recipient manually.
              </span>
            </div>
            <form onSubmit={createInvite} className="grid grid-cols-1 md:grid-cols-3 gap-3 items-end">
              <Field id="invemail" label="Email" className="md:col-span-1">
                <Input id="invemail" type="email" value={invEmail} onChange={(e) => setInvEmail(e.target.value)} required data-testid="invite-email-input" />
              </Field>
              <Field id="invrole" label="Role">
                <Select value={invRole} onValueChange={setInvRole}>
                  <SelectTrigger id="invrole" data-testid="invite-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="committee">Committee member</SelectItem>
                    <SelectItem value="owner">Lot owner</SelectItem>
                  </SelectContent>
                </Select>
              </Field>
              <Button type="submit" disabled={creatingInvite} style={{ background: "var(--blue)", color: "white" }} className="h-10 font-semibold" data-testid="invite-create-btn">
                <Plus size={14} className="mr-1.5" />
                {creatingInvite ? "Creating…" : "Create invite"}
              </Button>
              {invRole === "owner" && (
                <div className="md:col-span-3">
                  <Label className="text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--slate)" }}>
                    Lots owned by this person
                  </Label>
                  <div className="flex flex-wrap gap-1.5 mt-1.5">
                    {lots.map((lot) => (
                      <button
                        key={lot.lot_id}
                        type="button"
                        onClick={() => toggleLotForInvite(lot.lot_id)}
                        className="text-xs px-2.5 py-1 rounded-full border"
                        style={{
                          borderColor: invLotIds.includes(lot.lot_id) ? "var(--blue)" : "var(--border)",
                          background: invLotIds.includes(lot.lot_id) ? "var(--blue-light)" : "white",
                          color: invLotIds.includes(lot.lot_id) ? "var(--blue-dark)" : "var(--slate)",
                        }}
                        data-testid={`invite-lot-${lot.lot_number}`}
                      >
                        Lot {lot.lot_number}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </form>

            {lastInvite && (
              <div
                className="mt-4 rounded-lg border p-3"
                style={{ borderColor: "var(--border)", background: "var(--slate-light)" }}
                data-testid="invite-link-block"
              >
                <div className="text-[11px] uppercase tracking-wider mb-1.5" style={{ color: "var(--slate)" }}>
                  Invite link for {lastInvite.email}
                </div>
                <div className="flex items-center gap-2">
                  <code className="mono text-xs flex-1 truncate p-2 bg-white rounded border" style={{ borderColor: "var(--border)", color: "var(--navy)" }} data-testid="invite-link-value">
                    {lastInvite.full_url}
                  </code>
                  <Button type="button" variant="outline" size="sm" onClick={copyInvite} data-testid="invite-copy-btn">
                    {copied ? <Check size={14} /> : <Copy size={14} />}
                  </Button>
                </div>
                <p className="text-[11px] mt-2" style={{ color: "var(--tagline-grey)" }}>
                  Anyone with this link can accept the invite as <strong>{lastInvite.email}</strong> ({lastInvite.role}).
                </p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* ---- Members management ---- */}
        <Card style={{ borderColor: "var(--border)" }}>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Manage members</CardTitle>
            <CardDescription className="text-xs">Remove non-chair members from the scheme.</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="divide-y" style={{ borderColor: "var(--border)" }}>
              {members.map((m) => (
                <div key={m.membership_id} className="flex items-center justify-between py-3" data-testid={`member-row-${m.membership_id}`}>
                  <div>
                    <div className="font-medium text-sm" style={{ color: "var(--navy)" }}>
                      {m.name || m.email}
                    </div>
                    <div className="text-xs" style={{ color: "var(--slate)" }}>
                      {m.email} · <span style={{ textTransform: "uppercase", letterSpacing: "0.08em" }}>{m.role}</span> · {m.status}
                    </div>
                  </div>
                  {m.role !== "chair" && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeMember(m)}
                      style={{ color: "var(--alert)" }}
                      data-testid={`member-remove-${m.membership_id}`}
                    >
                      <X size={14} className="mr-1" />
                      Remove
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
      {/* Sprint 2H Phase B — MEMBER_INVITE confirmation gate. */}
      <ConfirmationGate {...inviteGate.gateProps} />
    </AppShell>
  );
}

function Field({ id, label, children, className = "" }) {
  return (
    <div className={`space-y-1.5 ${className}`}>
      <Label htmlFor={id} className="text-xs font-semibold uppercase tracking-wide" style={{ color: "var(--slate)", letterSpacing: "0.08em" }}>
        {label}
      </Label>
      {children}
    </div>
  );
}

function LotRow({ lot, onChange, onSave }) {
  return (
    <div className="grid grid-cols-12 gap-2 items-center py-1.5" data-testid={`lot-row-${lot.lot_number}`}>
      <div className="col-span-1 mono text-sm font-semibold text-center" style={{ color: "var(--navy)" }}>
        #{lot.lot_number}
      </div>
      <div className="col-span-3">
        <Input
          value={lot.address_unit || ""}
          onChange={(e) => onChange({ address_unit: e.target.value })}
          placeholder="Unit number"
          className="h-9 text-sm"
          data-testid={`lot-unit-${lot.lot_number}`}
        />
      </div>
      <div className="col-span-2">
        <Input
          type="number"
          min={0}
          value={lot.lot_entitlement ?? lot.unit_entitlement ?? 1}
          onChange={(e) => onChange({ lot_entitlement: e.target.value, unit_entitlement: e.target.value })}
          placeholder="Lot ent."
          className="h-9 text-sm"
          data-testid={`lot-ent-${lot.lot_number}`}
        />
      </div>
      <div className="col-span-2">
        <Input
          type="number"
          min={0}
          value={lot.interest_entitlement ?? 1}
          onChange={(e) => onChange({ interest_entitlement: e.target.value })}
          placeholder="Interest ent."
          className="h-9 text-sm"
          data-testid={`lot-int-${lot.lot_number}`}
        />
      </div>
      <div className="col-span-2">
        <Input
          value={lot.notes || ""}
          onChange={(e) => onChange({ notes: e.target.value })}
          placeholder="Notes"
          className="h-9 text-sm"
          data-testid={`lot-notes-${lot.lot_number}`}
        />
      </div>
      <div className="col-span-2 text-right">
        <Button variant="outline" size="sm" className="h-9" onClick={onSave} data-testid={`lot-save-${lot.lot_number}`}>
          Save
        </Button>
      </div>
    </div>
  );
}
